import { useState, useEffect, useMemo } from "react"
import { fetchStorefront } from "@app/scripts/lib/utils"
import {
  countryCurrency,
  useShopifyPriceData,
  BatchGraphQlResponse,
  SearchspringSearchResult,
} from "@app/types"

import { convertIdToProductVariantGid } from "@app/scripts/lib/shopify-utils"

/**
 * Merges searchspring results with updated pricing data
 * Returns a new array of products that have an updated price and compareAtPrice
 */
export const mergeResultsWithPricing = (
  results: (string | SearchspringSearchResult)[],
  priceData: Array<{
    handle: string
    id: string
    price: number
    compareAtPrice: number
  }>
): (string | SearchspringSearchResult)[] => {
  return results.map((result) => {
    // Adds call outs that were originally filtered out
    if (typeof result === "string") {
      return result
    }

    // Finds matching price data by handle
    const matchingPriceData = priceData.find(
      (priceItem) =>
        priceItem.handle === result.handle ||
        priceItem.handle === result?.attributes?.handle
    )

    if (matchingPriceData) {
      return {
        ...result,
        price: matchingPriceData.price.toString(),
        compareAtPrice: matchingPriceData.compareAtPrice.toString(),
      } as SearchspringSearchResult
    } else {
      // Set price to 0 if no price data found
      return {
        ...result,
        price: "null",
        compareAtPrice: "null",
      } as SearchspringSearchResult
    }
  })
}

export const useShopifyPrice = (
  formattedProductData: useShopifyPriceData[],
  currentCountryCurrency: countryCurrency
) => {
  const [priceData, setPriceData] = useState<
    Array<{ handle: string; id: string; price: number; compareAtPrice: number }>
  >([])

  // Initialize with default prices
  const defaultPrices = useMemo(() => {
    return formattedProductData.map((product) => ({
      handle: product.handle,
      id: product.id,
      price: product.price || 0,
      compareAtPrice: product.compareAtPrice || 0,
    }))
  }, [formattedProductData])

  useEffect(() => {
    const countryCode = currentCountryCurrency?.countryCode || "AU"

    if (countryCode === "AU") {
      setPriceData(defaultPrices)
      return
    }

    // Filter products with valid IDs and convert to GIDs
    const validProducts = formattedProductData
      .map((product) => ({
        ...product,
        gid: convertIdToProductVariantGid(product.id),
      }))
      .filter((product) => product.gid)

    if (validProducts.length === 0) {
      return
    }

    // Build GraphQL query for multiple products
    const gidsArray = validProducts.map((product) => `"${product.gid}"`)
    const gids = gidsArray.join(", ")
    const query = `
      query @inContext(country: ${countryCode}) {
        nodes(ids: [${gids}]) {
          ... on ProductVariant {
            id
            priceV2 {
              amount
              currencyCode
            }
            compareAtPriceV2 {
              amount
              currencyCode
            }
          }
        }
      }
    `

    fetchStorefront(query)
      .then((data: BatchGraphQlResponse) => {
        if (data?.nodes) {
          const priceMap = new Map()
          data.nodes.forEach((node) => {
            if (node?.id) {
              priceMap.set(node.id, {
                price: node?.priceV2?.amount
                  ? Number(node.priceV2.amount)
                  : null,
                compareAtPrice: node?.compareAtPriceV2?.amount
                  ? Number(node.compareAtPriceV2.amount)
                  : null,
              })
            }
          })

          const newPriceData = defaultPrices.map((defaultProduct) => {
            const gid = convertIdToProductVariantGid(defaultProduct.id)
            const apiPrices = gid ? priceMap.get(gid) : null

            return {
              handle: defaultProduct.handle,
              id: defaultProduct.id,
              price: apiPrices?.price ?? defaultProduct.price,
              compareAtPrice:
                apiPrices?.compareAtPrice ?? defaultProduct.compareAtPrice,
            }
          })

          setPriceData(newPriceData)
        } else {
          // Set price to 0 if no price data found
          setPriceData(
            defaultPrices.map((product) => ({
              ...product,
              price: "null",
              compareAtPrice: "null",
            }))
          )
        }
      })
      .catch((error) => {
        console.error("Failed to fetch batch variant prices:", error)
        // Set price to 0 if no price data found
        setPriceData(
          defaultPrices.map((product) => ({
            ...product,
            price: "null",
            compareAtPrice: "null",
          }))
        )
      })
  }, [formattedProductData, currentCountryCurrency?.currencyCode])

  return priceData
}
